import { observer } from 'mobx-react'
import { GetServerSideProps, NextPage } from 'next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-request'
import ChatHistory from '~/components/pages/deepseek/ChatHistory'
import { Layout } from '~/layouts/default'

import classNames from 'classnames'
import { useAtom } from 'jotai'
import {
  conversationIdAtom,
  messageListRefAtom,
  curHistoryConversationAtom,
  pollConversationIdsAtom,
} from '~/components/pages/deepseek/atom'
import { ChatMessageStatusEnum, ConversationType } from '~/graphqls/types'
import { GPT4oSilent } from '~/components/pages/gpt-4o/GPT4oSilent'
import GPT4oInput from '~/components/pages/gpt-4o/input'
import { useGPT4o } from '~/components/pages/gpt-4o/useGPT4o'
import { MessageList } from '~/components/pages/gpt-4o/MessageList'
import { useRouterChangeStart } from '~/hooks/useRouterChangeStart'
import { useResetAtoms } from '~/components/pages/deepseek/hooks'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { previewImageAtom } from '~/components/pages/gpt-4o/atom'
import { Close, Download, MirrorLoading } from 'wujieai-react-icon'
import { xhrDownload } from '~/utils'

const DeepSeek: NextPage<{}> = () => {
  const [previewImage, setPreviewImage] = useAtom(previewImageAtom)
  const [isDownloading, setIsDownloading] = useState(false)
  const [curHistoryConversation] = useAtom(curHistoryConversationAtom)
  const { messages, sendMessage, pollMessageStatus, stopMessage } = useGPT4o()
  const [pollConversationIds] = useAtom(pollConversationIdsAtom)
  const resetAtoms = useResetAtoms()

  const [currentTyping] = useMemo(() => {
    if (!messages.length) return [false]
    return [messages[messages.length - 1].status === ChatMessageStatusEnum.INIT]
  }, [messages])

  const chatTitle = useMemo(() => {
    const title =
      curHistoryConversation?.title ||
      messages[0]?.title ||
      messages[0]?.content ||
      '新会话'
    return title.length > 30 ? title.slice(0, 30) + '...' : title
  }, [curHistoryConversation, messages])

  const [, setMessageListRef] = useAtom(messageListRefAtom)

  const pollRef = useRef(0)
  const pollFn = async () => {
    await pollMessageStatus()
    pollRef.current = window.setTimeout(pollFn, 1000)
  }
  useEffect(() => {
    window.clearTimeout(pollRef.current)
    pollFn()
    return () => window.clearTimeout(pollRef.current)
  }, [pollConversationIds])

  useRouterChangeStart(async () => {
    resetAtoms()
  })

  return (
    <Layout
      headerType="no-margin"
      mainStyle={{
        height: 'calc(100vh - 56px)',
        minHeight: 'calc(100vh - 56px)',
      }}
      className="min-h-full "
      mainClassName="relative"
      headerClassName="border-b-[1px]  border-b-neutral-300"
      showFooter={false}
    >
      <SEOHead title="4o" />
      <div className="flex h-full">
        <ChatHistory
          stopMessage={stopMessage}
          conversationType={ConversationType.GPT}
        />
        <div className="flex-auto flex flex-col justify-center relative bg-neutral-100">
          <div
            className={classNames(
              'absolute left-0 top-0 right-0 bottom-0 overflow-y-auto scrollbar flex',
              messages.length !== 0 && 'justify-between'
            )}
            ref={ref => setMessageListRef(ref)}
          >
            <div
              className={classNames(
                'flex-auto h-fit flex flex-col items-center min-h-full',
                messages.length === 0 && 'justify-center'
              )}
            >
              {messages.length !== 0 ? (
                <>
                  <div
                    className="sticky top-0 z-10 w-full  text-base font-semibold text-neutral-900 text-center py-3  text-overflow"
                    style={{
                      background:
                        'linear-gradient(180deg, #141416 54.17%, rgba(20, 20, 22, 0.00) 100%)',
                    }}
                  >
                    {chatTitle}
                  </div>
                  <MessageList
                    messages={messages}
                    className="pt-12 w-[800px] flex-auto"
                  />
                </>
              ) : (
                <GPT4oSilent />
              )}
              <div className="sticky bottom-0 pb-6 bg-neutral-100 rounded-t-2xl w-[800px]">
                <GPT4oInput
                  sendMessage={sendMessage}
                  stopMessage={stopMessage}
                  isTyping={currentTyping}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Lightbox
        open={!!previewImage}
        close={() => setPreviewImage(undefined)}
        index={0}
        slides={[previewImage || ''].map((item, index) => ({
          src: item,
        }))}
        animation={{ fade: 250, swipe: 500 }}
        styles={{
          container: {
            background: 'rgba(0,0,0, 0.5)',
            backdropFilter: 'blur(8px)',
          },
        }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
          buttonClose: () => (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full border-2 border-neutral-500 text-neutral-500 flex items-center justify-center cursor-pointer bg-black shadow-md"
                onClick={async () => {
                  if (isDownloading) return
                  try {
                    setIsDownloading(true)
                    await xhrDownload(previewImage || '', '4o-image', 'image')
                  } finally {
                    setIsDownloading(false)
                  }
                }}
              >
                {isDownloading ? (
                  <MirrorLoading size={20} className="animate-spin" />
                ) : (
                  <Download size={20} />
                )}
              </div>
              <div
                className="w-10 h-10 rounded-full border-2 border-neutral-500 text-neutral-500 flex items-center justify-center cursor-pointer bg-black shadow-md"
                onClick={() => {
                  setPreviewImage('')
                }}
              >
                <Close size={20} />
              </div>
            </div>
          ),
        }}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { client, initialState, isLogin, loginRedirect } = await GlobalRequest(
    context
  )

  if (!isLogin) {
    return loginRedirect()
  }

  return {
    props: {
      initialState,
    },
  }
}

export default observer(DeepSeek)
